import React, { useState } from 'react';
import { getScopeFromDate } from './timeScopes';

// DJB-195 (r2): the line graph below the Transactions blocks. The blocks are
// a single-select group — the one selected payment type is drawn as a single
// line in the selection blue; with nothing selected the graph area shows a
// placeholder. Axes: x = time, spanning the widget's full scope range
// (1d/3d/7d/30d plus, since DJB-212, YTD and 1yr — only Lifetime disables the
// graph in TransactionsWidget), y = the number of transactions per bucket.
// The data comes from /statistics/payments/series-by-type (DJB-193), which
// zero-fills every bucket for every payment type across the requested range.

// Fixed drawing coordinates; the SVG scales to the widget width via viewBox
const VB_W = 640;
const VB_H = 240;
const PLOT = { top: 10, right: 14, bottom: 26, left: 52 };
const PLOT_W = VB_W - PLOT.left - PLOT.right;
const PLOT_H = VB_H - PLOT.top - PLOT.bottom;
const Y_DIVISIONS = 4;
const X_LABEL_TARGET = 5;

// The backend serializes the LocalDateTime bucketStart as
// [y, m, d, h, min(, s, nanos)] like the machine timestamps (the graph not
// rendering at all traced back to feeding that array straight into
// new Date()); tolerate ISO strings should that config change.
const toBucketDate = (bucketStart) => {
  if (Array.isArray(bucketStart)) {
    return new Date(
      bucketStart[0], bucketStart[1] - 1, bucketStart[2],
      bucketStart[3] || 0, bucketStart[4] || 0, bucketStart[5] || 0
    );
  }
  return new Date(bucketStart);
};

// Mirrors the backend's bucket derivation (DashboardStatisticsController
// deriveBucket) — the request omits the bucket param, so time labels must
// assume the granularity the server picks for the scope's range. The server
// counts whole days (ChronoUnit.DAYS truncates), hence the floor: YTD sits
// right on the 92-day edge early in April and 1yr on 365/366 → month.
export const bucketGranularity = (scopeKey, now = new Date()) => {
  const days = Math.floor(
    (now.getTime() - getScopeFromDate(scopeKey, now).getTime()) / (24 * 60 * 60 * 1000)
  );
  if (days <= 3) return 'hour';
  if (days <= 92) return 'day';
  if (days <= 731) return 'month';
  return 'year';
};

const two = (n) => String(n).padStart(2, '0');

const axisTimeLabel = (date, granularity) => {
  if (granularity === 'hour') return `${two(date.getHours())}:00`;
  if (granularity === 'day') return `${two(date.getDate())}/${two(date.getMonth() + 1)}`;
  if (granularity === 'month') {
    return `${date.toLocaleString('nl-BE', { month: 'short' })} ${date.getFullYear()}`;
  }
  return String(date.getFullYear());
};

const tooltipTimeLabel = (date, granularity) => {
  if (granularity === 'hour') {
    return `${two(date.getDate())}/${two(date.getMonth() + 1)} ${two(date.getHours())}:00`;
  }
  if (granularity === 'day') {
    return `${two(date.getDate())}/${two(date.getMonth() + 1)}/${date.getFullYear()}`;
  }
  return axisTimeLabel(date, granularity);
};

// 1 / 2 / 5 × 10^k ≥ value with a floor of 1, so the y ticks land on whole
// transaction counts
const niceCountStep = (value) => {
  if (value <= 1) return 1;
  const base = Math.pow(10, Math.floor(Math.log10(value)));
  const frac = value / base;
  if (frac <= 1) return base;
  if (frac <= 2) return 2 * base;
  if (frac <= 5) return 5 * base;
  return 10 * base;
};

const round2 = (value) => Math.round(value * 100) / 100;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

export const TransactionsGraph = ({ data, loading, error, selectedType, scopeKey }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  if (error) {
    return (
      <div className="widget-empty-note">The transactions graph is unavailable right now.</div>
    );
  }
  // DJB-195 r2: nothing selected is the default state — placeholder, no plot
  if (!selectedType) {
    return <div className="widget-empty-note">Select to show data</div>;
  }
  if (!data) {
    // first load — no previous frame to keep on screen yet
    return loading ? <div className="widget-empty-note">…</div> : null;
  }

  const entry = data.find((item) => item.paymentType === selectedType.enumName);
  const buckets = (entry && entry.buckets) || [];
  const times = buckets.map((bucket) => toBucketDate(bucket.bucketStart));
  const bucketCount = times.length;

  if (bucketCount === 0) {
    return <div className="widget-empty-note">No transactions in this period.</div>;
  }

  // The x axis spans the scope's full range (from → now), not just the
  // buckets, so the toggle's duration reads directly off the axis. Bucket
  // starts are floored to their granularity, so the first one can land just
  // before the range — clamp instead of drawing outside the plot.
  const now = new Date();
  const domainStart = getScopeFromDate(scopeKey, now).getTime();
  const domainSpan = Math.max(1, now.getTime() - domainStart);
  const xFor = (index) =>
    round2(PLOT.left + clamp01((times[index].getTime() - domainStart) / domainSpan) * PLOT_W);

  const maxCount = Math.max(0, ...buckets.map((bucket) => bucket.count || 0));
  // flat-zero data still gets a 0–4 frame instead of a degenerate axis
  const step = niceCountStep((maxCount || Y_DIVISIONS) / Y_DIVISIONS);
  const yMax = step * Math.ceil((maxCount || Y_DIVISIONS) / step);
  const yTicks = [];
  for (let i = 0; i * step <= yMax; i += 1) yTicks.push(i * step);
  const yFor = (count) => round2(PLOT.top + PLOT_H - (count / yMax) * PLOT_H);

  const xStride = Math.max(1, Math.ceil(bucketCount / X_LABEL_TARGET));
  const xTickIndexes = times.map((_, i) => i).filter((i) => i % xStride === 0);

  // stale hover index can outlive a shrinking dataset (scope change)
  const active = activeIndex === null ? null : Math.min(activeIndex, bucketCount - 1);

  const moveActive = (updater) =>
    setActiveIndex((prev) => {
      const current = prev === null ? bucketCount - 1 : Math.min(prev, bucketCount - 1);
      return updater(current);
    });

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width) return;
    const vx = ((event.clientX - rect.left) / rect.width) * VB_W;
    let nearest = 0;
    for (let i = 1; i < bucketCount; i += 1) {
      if (Math.abs(xFor(i) - vx) < Math.abs(xFor(nearest) - vx)) nearest = i;
    }
    setActiveIndex(nearest);
  };

  const handleKeyDown = (event) => {
    const handlers = {
      ArrowRight: () => moveActive((i) => Math.min(bucketCount - 1, i + 1)),
      ArrowLeft: () => moveActive((i) => Math.max(0, i - 1)),
      Home: () => setActiveIndex(0),
      End: () => setActiveIndex(bucketCount - 1),
      Escape: () => setActiveIndex(null),
    };
    if (handlers[event.key]) {
      event.preventDefault();
      handlers[event.key]();
    }
  };

  const granularity = bucketGranularity(scopeKey, now);
  const tooltipLeftPct = active === null ? 0 : (xFor(active) / VB_W) * 100;

  return (
    <div className={`transactions-graph ${loading ? 'is-loading' : ''}`.trim()}>
      <div
        className="transactions-graph-frame"
        tabIndex={0}
        role="group"
        aria-label={`Transactions graph: number of ${selectedType.label} transactions over time. Use the arrow keys to read the values per time bucket.`}
        onKeyDown={handleKeyDown}
        onFocus={() => setActiveIndex((prev) => (prev === null ? bucketCount - 1 : prev))}
        onBlur={() => setActiveIndex(null)}
      >
        <svg
          className="transactions-graph-svg"
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          aria-hidden="true"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setActiveIndex(null)}
        >
          {/* y measures transaction counts, not a currency value */}
          <text
            className="tx-axis-text tx-axis-label"
            transform={`rotate(-90 11 ${PLOT.top + PLOT_H / 2})`}
            x={11}
            y={PLOT.top + PLOT_H / 2}
            textAnchor="middle"
          >
            Transactions
          </text>
          {yTicks.map((tick) => (
            <g key={tick}>
              <line
                className={tick === 0 ? 'tx-baseline' : 'tx-gridline'}
                x1={PLOT.left}
                x2={PLOT.left + PLOT_W}
                y1={yFor(tick)}
                y2={yFor(tick)}
                vectorEffect="non-scaling-stroke"
              />
              <text
                className="tx-axis-text"
                x={PLOT.left - 8}
                y={yFor(tick)}
                textAnchor="end"
                dominantBaseline="middle"
              >
                {tick}
              </text>
            </g>
          ))}
          {xTickIndexes.map((index) => (
            <text
              key={times[index].getTime()}
              className="tx-axis-text"
              x={xFor(index)}
              y={VB_H - 8}
              textAnchor="middle"
            >
              {axisTimeLabel(times[index], granularity)}
            </text>
          ))}
          {active !== null && (
            <line
              className="tx-crosshair"
              x1={xFor(active)}
              x2={xFor(active)}
              y1={PLOT.top}
              y2={PLOT.top + PLOT_H}
              vectorEffect="non-scaling-stroke"
            />
          )}
          <g data-series={selectedType.enumName} className="tx-series">
            {bucketCount > 1 ? (
              <path
                className="tx-series-line"
                vectorEffect="non-scaling-stroke"
                d={buckets
                  .map((bucket, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(bucket.count || 0)}`)
                  .join(' ')}
              />
            ) : (
              <circle
                className="tx-series-marker"
                cx={xFor(0)}
                cy={yFor(buckets[0].count || 0)}
                r="4.5"
              />
            )}
            {active !== null && bucketCount > 1 && (
              <circle
                className="tx-series-marker"
                cx={xFor(active)}
                cy={yFor(buckets[active].count || 0)}
                r="4.5"
              />
            )}
          </g>
        </svg>
        {active !== null && (
          <div
            className={`transactions-graph-tooltip ${tooltipLeftPct > 55 ? 'is-left' : ''}`.trim()}
            style={{ left: `${tooltipLeftPct}%` }}
          >
            <div className="transactions-graph-tooltip-time">
              {tooltipTimeLabel(times[active], granularity)}
            </div>
            <div className="transactions-graph-tooltip-row">
              <span className="tx-line-key" />
              <span className="transactions-graph-tooltip-value">
                {`${buckets[active].count || 0}x`}
              </span>
              <span className="transactions-graph-tooltip-label">{selectedType.label}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsGraph;
